import type { Estimator } from '../api'
import { pp } from './scale'

/**
 * What a campaign should do about this poll, in the imperative.
 *
 * ## Why a console should say this at all
 *
 * Everything else on the board describes. A candidate reads a two-point lead and then has to
 * decide whether to move a week of money, and nothing on the screen helped them with the
 * actual decision — so the decision got made on instinct, which is what the poll was bought
 * to replace.
 *
 * These are the four moves that follow mechanically from what the poll says, and each one is
 * tied to the served flag that triggers it. They are NOT predictions and they are not
 * strategy: a line appears because a specific number crossed a specific line, and the line is
 * named so nobody mistakes it for a view about the race.
 *
 * ## Deliberately short, and never more than three
 *
 * A list of nine recommendations is a list nobody reads. If more than three apply, the three
 * that appear are the ones with the shortest fuse — a poll you cannot trust comes before a
 * weak ward, because acting on an untrustworthy poll is the more expensive mistake.
 */
export function NextMove({ data }: { data: Estimator }) {
  const lead = data.separations[0] ?? null
  const rate = data.attrition?.responseRate1 ?? null
  const thin = data.subgroups.filter((s) => s.thin)
  const moves: { do: string; why: string }[] = []

  // 1. An untrustworthy poll first: every other move below would be made on bad information.
  if (rate !== null && rate < 0.2) {
    moves.push({
      do: 'Do not make a spending decision on this poll.',
      why: `Only about ${Math.round(rate * 10)} in 10 people we approached took part. `
        + 'Field more interviews before this moves any money.',
    })
  }

  // 2. The gap, and the only two honest readings of it.
  if (lead && lead.gap !== null && lead.separated === false) {
    moves.push({
      do: 'Treat this as a tie and run it again.',
      why: `The ${pp(Math.abs(lead.gap))}-point gap is inside this poll's own error, so the `
        + 'order could flip on a repeat. A second wave is cheaper than acting on a gap that '
        + 'is not there.',
    })
  } else if (lead && lead.gap !== null && lead.separated) {
    moves.push({
      do: `Defend the lead — do not chase ${lead.bLabel}.`,
      why: `${lead.aLabel} is genuinely ahead on this poll. The spend that protects a real `
        + 'lead is turnout in your own strong areas, not persuasion in theirs.',
    })
  }

  // 3. Blind spots, named, because "insufficient base" is not an instruction.
  if (thin.length > 0) {
    const named = thin.slice(0, 2)
      .map((s) => `${s.dimensionCode.toLowerCase()} ${s.valueCode.toLowerCase()}`)
      .join(' and ')
    moves.push({
      do: `Commission more interviews in ${named}.`,
      why: `There are too few interviews there to say anything${thin.length > 2
        ? `, and in ${thin.length - 2} other group${thin.length - 2 === 1 ? '' : 's'}` : ''}. `
        + 'Any plan built on those numbers is built on nothing.',
    })
  }

  if (moves.length === 0) {
    return (
      <p className="px-4 py-3 text-erp-sm text-muted-foreground">
        Nothing in this poll demands a change of plan: the figures are solid, the gap is
        readable, and no group is too thin to report.
      </p>
    )
  }

  return (
    <ol className="space-y-2.5 px-4 py-3">
      {moves.slice(0, 3).map((m, i) => (
        <li key={i} className="text-erp-sm">
          <span className="font-medium">{m.do}</span>{' '}
          <span className="text-muted-foreground">{m.why}</span>
        </li>
      ))}
    </ol>
  )
}
